"use client";

import type { RefObject } from "react";
import { BackArrowIcon } from "./paletteIcons";

/** KB Direct Search Screen */
export function KnowledgeSearchView({
  query,
  onQueryChange,
  onSubmit,
  onBack,
  pages,
  loading,
  onOpenPage,
  inputRef,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  /** Enter in the field: open the page named by the query. */
  onSubmit: () => void;
  onBack: () => void;
  pages: string[];
  loading: boolean;
  onOpenPage: (slug: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
}) {
  return (
    <div className="flex flex-col">
      <header className="flex items-center gap-3 border-b border-zinc-800 px-4 py-3.5">
        <button
          type="button"
          onClick={() => onBack()}
          className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
          title="Back to command list"
        >
          <BackArrowIcon />
        </button>
        <svg
          viewBox="0 0 16 16"
          fill="none"
          className="size-5 shrink-0 text-violet-400"
        >
          <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.3" />
          <path
            d="M10.5 10.5 14 14"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="flex-1"
        >
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search knowledge base pages (e.g. pricing, faq, competitors)..."
            className="w-full bg-transparent text-sm text-zinc-100 placeholder:text-zinc-500 outline-none"
          />
        </form>
        <kbd className="rounded border border-zinc-800 bg-zinc-900 px-1.5 py-0.5 text-[10px] font-medium text-zinc-400">
          ↵ to open
        </kbd>
      </header>

      <div className="max-h-80 overflow-y-auto p-2">
        <div className="px-3 py-1.5 text-[11px] font-semibold tracking-wider text-zinc-500 uppercase">
          Available KB Pages
        </div>
        {loading && (
          <div className="px-3 py-4 text-xs text-zinc-500">Loading pages…</div>
        )}
        {!loading && pages.length === 0 && (
          <div className="px-3 py-4 text-xs text-zinc-500">No KB pages found.</div>
        )}
        {pages
          .filter(
            (p) =>
              !query ||
              p.toLowerCase().includes(query.trim().toLowerCase())
          )
          .map((page) => {
            const slug = page.replace(/\.md$/, "");
            return (
              <button
                key={page}
                type="button"
                onClick={() => onOpenPage(slug)}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-zinc-300 transition hover:bg-zinc-900 hover:text-white"
              >
                <div className="flex items-center gap-2">
                  <svg viewBox="0 0 16 16" fill="none" className="size-4 text-violet-400">
                    <path
                      d="M8 3.5C7 2.7 5.5 2.25 3.5 2.25c-.4 0-.75.05-1 .1V12.6c.25-.05.6-.1 1-.1 2 0 3.5.45 4.5 1.25 1-.8 2.5-1.25 4.5-1.25.4 0 .75.05 1 .1V2.35c-.25-.05-.6-.1-1-.1-2 0-3.5.45-4.5 1.25Z"
                      stroke="currentColor"
                      strokeWidth="1.25"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{slug}</span>
                </div>
                <span className="text-xs text-zinc-500">Wiki page</span>
              </button>
            );
          })}
      </div>
    </div>
  );
}
